import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import Documents from './Documents';
import NotificationDialog from '../../../HeaderContainer/Popup/NotificationDialog';
import { ReactComponent as MySpinner } from '../../../../animations/mySpinner.svg';
import { useUploadDocumentsMutation } from '../../../../features/master/masterApiSlice';
import { documentFormats, isValidDocumentFile } from '../../../../utils/fileValidationFunctions';

const emptyFiles = { passport1: null, passport2: null, itn: null };

const UploadDocuments = ({ existingDocuments = [] }) => {
    const { t } = useTranslation();
    const [files, setFiles] = useState(emptyFiles);
    const [documentType, setDocumentType] = useState('id_card');
    const [notificationText, setNotificationText] = useState('');
    const [isOpen, setIsOpen] = useState(false);
    const [uploadDocuments, { isLoading }] = useUploadDocumentsMutation();

    const previews = useMemo(() => Object.entries(files).filter(([, file]) => file).map(([kind, file]) => ({
        kind, fileName: file.name, url: URL.createObjectURL(file),
        mime: file.type || (file.name.toLowerCase().endsWith('.pdf') ? 'application/pdf' : ''), local: true
    })), [files]);

    useEffect(() => () => previews.forEach(preview => URL.revokeObjectURL(preview.url)), [previews]);

    const showError = key => {
        setNotificationText(key);
        setIsOpen(true);
    };

    const validate = file => {
        const result = isValidDocumentFile(file);
        if (!result.isValid) showError(result.error);
        return result.isValid;
    };

    const selectPassport = (slot, event) => {
        const selected = Array.from(event.target.files || []);
        event.target.value = '';
        if (selected.length > 2) return showError('TooManyPassportFiles');
        if (!selected.length || !selected.every(validate)) return;
        setFiles(previous => {
            const next = { ...previous, [slot]: selected[0] };
            if (slot === 'passport1' && selected[1]) next.passport2 = selected[1];
            return next;
        });
    };

    const selectItn = event => {
        const file = event.target.files?.[0];
        event.target.value = '';
        if (!file || !validate(file)) return;
        setFiles(previous => ({ ...previous, itn: file }));
    };

    const removeFile = kind => setFiles(previous => ({ ...previous, [kind]: null }));

    const onSubmit = async event => {
        event.preventDefault();
        const alreadyHasPassport = existingDocuments.some(document => document.kind === 'passport1');
        if (!files.passport1 && !alreadyHasPassport) return showError('PassportRequired');
        if (!Object.values(files).some(Boolean)) return showError('DocumentFileRequired');
        const formData = new FormData();
        formData.append('documentType', documentType);
        Object.entries(files).forEach(([key, file]) => file && formData.append(key, file));
        try {
            const result = await uploadDocuments(formData).unwrap();
            if (result?.success !== true) throw new Error('UploadDocError');
            setFiles(emptyFiles);
            setNotificationText('DocumentsSentForReview');
            setIsOpen(true);
        } catch (error) {
            showError(error?.data?.error || 'UploadDocError');
        }
    };

    const accept = '.jpg,.jpeg,.png,.webp,.heic,.heif,.pdf,image/jpeg,image/png,image/webp,image/heic,image/heif,application/pdf';
    const uploadBox = (kind, label, multiple = false) => <div className="col-xl-6 col-md-6 margin-bottom-15">
        <div className="uploadButton margin-bottom-0">
            <input id={`document-${kind}`} className="uploadButton-input" type="file" accept={accept}
                multiple={multiple} onChange={event => selectPassport(kind, event)} />
            <label className="uploadButton-button ripple-effect" htmlFor={`document-${kind}`}>{label}</label>
        </div>
        {files[kind] && <div className="margin-top-10">
            <span className="uploadButton-file-name">{files[kind].name}</span>
            <button type="button" className="button gray small margin-left-10" onClick={() => removeFile(kind)}>{t('Remove')}</button>
        </div>}
    </div>;

    return <div className="col-xl-12">
        <p><i className="icon-feather-info"></i>&nbsp;{t('DocumentUploadCondition')} <i>{documentFormats.join(', ')}</i></p>
        <form onSubmit={onSubmit}>
            <div className="submit-field">
                <h5>{t('DocumentType')}</h5>
                <select className="with-border" value={documentType} onChange={event => setDocumentType(event.target.value)}>
                    <option value="id_card">{t('IdCard')}</option>
                    <option value="passport_booklet">{t('PassportBooklet')}</option>
                    <option value="international_passport">{t('InternationalPassport')}</option>
                </select>
            </div>
            <p>{t('UploadPassportSidesHint')}</p>
            <div className="row">
                {uploadBox('passport1', t('PassportFrontSide'), true)}
                {uploadBox('passport2', t('PassportBackSide'))}
            </div>
            <div className="row">
                <div className="col-xl-6 col-md-6 margin-bottom-15">
                    <div className="uploadButton margin-bottom-0">
                        <input id="document-itn" className="uploadButton-input" type="file" accept={accept} onChange={selectItn} />
                        <label className="uploadButton-button ripple-effect" htmlFor="document-itn">{t('ItnOptional')}</label>
                    </div>
                    {files.itn && <div className="margin-top-10">
                        <span className="uploadButton-file-name">{files.itn.name}</span>
                        <button type="button" className="button gray small margin-left-10" onClick={() => removeFile('itn')}>{t('Remove')}</button>
                    </div>}
                </div>
            </div>
            {!!previews.length && <Documents documents={previews} />}
            <button className="button big ripple-effect margin-top-15" type="submit" disabled={isLoading}>
                {isLoading ? <><MySpinner style={{ display: 'inline-block', marginBottom: '-10px' }} /> {t('Uploading')}</> : t('Upload')}
            </button>
        </form>
        <NotificationDialog open={isOpen} onClose={() => setIsOpen(false)}>{t(notificationText)}</NotificationDialog>
    </div>;
};

export default UploadDocuments;
